import { ChevronLeft, MapPin, Plus, Check } from "lucide-react";
import { Button } from "@/app/components/button";
import { CheckoutProgress } from "@/app/components/checkout-progress";
import { formatCEP } from "@/config/locale";

/** Coerce to string to avoid rendering objects as React children (error #300). */
function safeText(x: unknown): string {
  if (x == null) return "";
  if (typeof x === "string" || typeof x === "number") return String(x);
  return "";
}

export interface Address {
  id: string;
  name: string;
  street: string;
  number: string;
  complement?: string;
  neighborhood: string;
  city: string;
  state: string;
  zipCode: string;
  isDefault?: boolean;
}

interface CheckoutAddressPageProps {
  onBack: () => void;
  addresses: Address[];
  selectedAddressId: string | null;
  onSelectAddress: (addressId: string) => void;
  onAddNewAddress: () => void;
  onContinue: () => void;
}

export function CheckoutAddressPage({
  onBack,
  addresses,
  selectedAddressId,
  onSelectAddress,
  onAddNewAddress,
  onContinue,
}: CheckoutAddressPageProps) {
  return (
    <div className="min-h-screen bg-background pb-8">
      {/* Header */}
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <div>
              <h1 className="text-xl text-white">Endereço de Entrega</h1>
              <p className="text-sm text-white/80">Escolha onde deseja receber</p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Progress Indicator */}
        <CheckoutProgress currentStep={1} stepLabel="Endereço" totalSteps={3} />

        {/* Address List – existing addresses on top; default first */}
        <div className="space-y-4 mb-4">
          {[...addresses].sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0)).map((address) => (
            <button
              key={address.id}
              onClick={() => onSelectAddress(address.id)}
              className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                selectedAddressId === address.id
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50"
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  selectedAddressId === address.id
                    ? "border-primary bg-primary"
                    : "border-border"
                }`}>
                  {selectedAddressId === address.id && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <MapPin className="w-4 h-4 text-primary" />
                    <span className="font-semibold">{safeText(address.name)}</span>
                    {address.isDefault && (
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                        Padrão
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {safeText(address.street)}, {safeText(address.number)}
                    {address.complement != null && safeText(address.complement) !== "" && `, ${safeText(address.complement)}`}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {safeText(address.neighborhood)}, {safeText(address.city)} - {safeText(address.state)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    CEP: {formatCEP(safeText(address.zipCode))}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Add New Address */}
        <button
          onClick={onAddNewAddress}
          className="w-full mb-6 p-4 border-2 border-dashed border-border hover:border-primary hover:bg-primary/5 rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          <Plus className="w-5 h-5 text-primary" />
          <span className="font-medium text-primary">Adicionar Novo Endereço</span>
        </button>

        {/* Continue Button */}
        <Button
          onClick={onContinue}
          disabled={!selectedAddressId}
          className="w-full"
          size="lg"
        >
          Continuar para Opções de Envio
        </Button>
      </div>
    </div>
  );
}